import { ChatBotType } from "./ChatWidget";

/**
 * The suggestion chips under the welcome message. The patient bot's are the two questions that were
 * hard-coded into the widget before CLE-2811, kept verbatim. The Hospital Staff Bot shows none.
 */
export const PATIENT_FALLBACK_QUESTIONS = [
  "Why do I owe this amount?",
  "What's included in this estimate?",
];

/**
 * The patient chatbot is live in production and frozen to the exact two questions the widget has
 * always shown: nothing a mount or a model's config says can reach it, so no admin edit can change
 * what a patient sees. Every other chat type — today only the Hospital Staff Bot — shows no chips
 * (2026-10-29, Ani after the demo: they mixed how-to questions with the old contract questions and
 * "did not present well"). A model's `config.suggestedPrompts` is still stored by the API, but the
 * widget never reads it.
 */
export function resolveSuggestedQuestions(
  chatType: ChatBotType | string
): string[] {
  return chatType === "PatientChatBot" ? PATIENT_FALLBACK_QUESTIONS : [];
}

/**
 * CLE-2851 — contract chats belong to the user who started them, so a `chatId` in the URL (a link a
 * colleague shared, or one left over from another login) can name a chat this user's list does not
 * contain. True when that chatId should be dropped from the URL so the widget starts fresh instead of
 * pointing at a chat it can never open.
 */
export const isUnlistedChatId = (
  chatIdFromUrl: string | null,
  chatList: { id: string }[]
): boolean =>
  !!chatIdFromUrl && !chatList.some((chat) => chat.id === chatIdFromUrl);

/**
 * Whether the widget shows its chat-history list on the left. Never for the Hospital Staff Bot
 * (2026-10-29, Ani: the list is hidden for staff; the header's "+ New chat" replaces its button).
 * Otherwise the model's `multipleChatsSupported`, which is null until it is known.
 */
export const showsChatList = (
  chatType: ChatBotType | string,
  multipleChatsSupported: boolean | null | undefined
): boolean =>
  chatType !== "ContractManagementBot" && multipleChatsSupported === true;

/**
 * Whether the header shows "+ New chat" (2026-10-29): the staff bot only — it has no chat list, so
 * its New chat lives here — and only when there is a chat to leave (one is open, or the conversation
 * has messages). On a fresh, empty widget the button would do nothing. Fixed or inline alike: without
 * it an inline staff mount would be stuck on an ended (CLOSED) chat.
 */
export const showsHeaderNewChat = ({
  chatType,
  currentChatId,
  messageCount,
}: {
  chatType: ChatBotType | string;
  currentChatId: string | null | undefined;
  messageCount: number;
}): boolean =>
  chatType === "ContractManagementBot" && (!!currentChatId || messageCount > 0);

/**
 * Whether loading the chat list auto-opens its newest chat, for a model that keeps one chat per user
 * (`multipleChatsSupported === false`). Never for the staff bot (2026-10-29): its restore effect
 * decides which chat to re-open (pickChatToRestore), and an auto-selected newest chat — possibly an
 * ended one — would otherwise win over the stored last chat.
 */
export const autoSelectsFirstChat = (
  chatType: ChatBotType | string,
  multipleChatsSupported: boolean
): boolean => chatType !== "ContractManagementBot" && !multipleChatsSupported;

/**
 * Whether the restore effect should (re-)open the last selected chat. Not when that exact chat, from
 * this same list snapshot, is already the one open: the effect re-runs because opening it changed
 * the last selected id, and selecting it again would reset its messages, rewrite the URL and queue a
 * second focus a second time. A freshly fetched list (new objects) still reloads it, as before.
 */
export const shouldReopenLastChat = <T extends { id: string }>({
  lastChat,
  currentChatId,
  loadedChat,
}: {
  lastChat: T | null | undefined;
  currentChatId: string | null;
  loadedChat: T | null;
}): boolean =>
  !!lastChat && !(lastChat.id === currentChatId && lastChat === loadedChat);
